import { useMutation, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import http from '@/services/http';
import { batchedModelRecordQueryKey } from '@/composables/api/useBatchedModelRecord';
import type { SchedulePayload, ScheduleRow } from '@/utils/customPlaylistSchedule';
import {
    NESTED_SCHEDULES_FIELD,
    recordScheduleDeleted,
    recordScheduleSaved,
    removeScheduleItem,
    upsertScheduleItem,
} from './scheduleItems';

export {
    NESTED_SCHEDULE_KEYS,
    NESTED_SCHEDULES_FIELD,
    removeScheduleItem,
    toNestedScheduleItem,
    upsertScheduleItem,
} from './scheduleItems';

/**
 * Create, edit and delete one custom playlist schedule, and keep every cached
 * copy of the parent list in step.
 *
 *   POST   /api/custom_playlist_schedule        -> 201 { data: row }
 *   PUT    /api/custom_playlist_schedule/{id}   -> 200 { data: row }
 *   DELETE /api/custom_playlist_schedule/{id}   -> 200 (a hard delete: forceDelete)
 *
 * The table rows and the inspector are the LIST's search document, whose
 * nested `location_zone_custom_playlist_schedules` carries each schedule. On
 * staging and production that document is reindexed by a queued job, so a
 * refetch straight after the write usually returns the old document and the
 * save would seem to vanish. So after every write:
 *
 * 1. The write is recorded as pending on its list (scheduleItems.ts). Every
 *    surface that shows a list's schedules reads them through that overlay, so
 *    the save survives a live-poll tick that brings the old document back, a
 *    remounted drawer or inspector tab, and a row snapshot no cache reaches.
 * 2. The saved row is patched into the list's nested schedules in every cached
 *    search page, every cached live-poll row set, and the list's inspector
 *    record (amendment A3).
 * 3. The search and record keys are invalidated WITHOUT an immediate refetch
 *    (`refetchType: 'none'`), so the next page change, sort, filter or re-open
 *    fetches the reindexed document. The live poll still ticks every 30 seconds
 *    and may bring the old document back; step 1 is what keeps the save shown.
 *
 * A saved row lands on the list the SERVER put it on. A new silence row is
 * attached to the zone's "Silence Schedule" list, not the list the dialog was
 * opened from, so it is patched onto that list and never onto the opener.
 *
 * No schedule_history row is written from here (amendment A4).
 */

export const SCHEDULE_RESOURCE = '/api/custom_playlist_schedule';

/** Every cached search page of custom lists (the table rows are lists, not schedules). */
export const LIST_SEARCH_QUERY_KEY = ['search', 'location_zone_custom_playlist'] as const;

/** The Search page's live poll of the visible custom lists; its data is a bare row array. */
export const LIST_LIVE_POLL_QUERY_KEY = ['search-live-poll', 'location_zone_custom_playlist'] as const;

/**
 * Lists fetched by id for a drawer (useModelRecordsByIds), such as a zone's
 * Custom Lists drawer. Each entry's data is a Map of list id to list document.
 */
export const LIST_RECORDS_BY_ID_QUERY_KEY = ['model-records', 'LocationZoneCustomPlaylist'] as const;

/** The inspector's cached copy of one list. */
export function listRecordQueryKey(listId: number | string) {
    return ['model-record', 'LocationZoneCustomPlaylist', String(listId)] as const;
}

interface ApiErrorShape {
    response?: {
        status?: number;
        data?: {
            message?: string;
            errors?: Record<string, string[]>;
        };
    };
    message?: string;
}

/**
 * The message a failed write should show: the first field error of a 422, or
 * else the server's `message`. Null when the error carries no response body.
 */
export function extractApiErrorMessage(error: unknown): string | null {
    const body = (error as ApiErrorShape | undefined)?.response?.data;
    if (!body) return null;
    if (body.errors) {
        const firstField = Object.keys(body.errors)[0];
        const firstMessage = firstField ? body.errors[firstField]?.[0] : undefined;
        if (firstMessage) return firstMessage;
    }
    return typeof body.message === 'string' && body.message !== '' ? body.message : null;
}

export const SCHEDULE_FORBIDDEN_MESSAGE = "You don't have permission to change this zone's schedules.";

/** Every cached page of the schedule search, whose rows are schedules. */
export const SCHEDULE_SEARCH_QUERY_KEY = ['search', 'custom_playlist_schedule'] as const;

/** The Search page's live poll of the visible schedules; its data is a bare row array. */
export const SCHEDULE_LIVE_POLL_QUERY_KEY = ['search-live-poll', 'custom_playlist_schedule'] as const;

/**
 * The schedule search's own rows. A schedule edited from its card there is drawn from its
 * search row, so the saved row is merged into it, and a deleted one leaves the page.
 * `saved` null removes the row.
 */
export function patchCachedScheduleSearchRows(queryClient: QueryClient, scheduleId: number, saved: Record<string, unknown> | null): void {
    const patchRows = (rows: unknown[]): unknown[] =>
        saved === null
            ? rows.filter((row) => !(isRecord(row) && Number(row.id) === scheduleId))
            : rows.map((row) => (isRecord(row) && Number(row.id) === scheduleId ? { ...row, ...saved } : row));
    const holds = (rows: unknown[]) => rows.some((row) => isRecord(row) && Number(row.id) === scheduleId);
    queryClient.setQueriesData({ queryKey: SCHEDULE_SEARCH_QUERY_KEY }, (page: unknown) => {
        if (!isRecord(page) || !Array.isArray(page.data) || !holds(page.data)) return page;
        return { ...page, data: patchRows(page.data) };
    });
    queryClient.setQueriesData({ queryKey: SCHEDULE_LIVE_POLL_QUERY_KEY }, (rows: unknown) =>
        Array.isArray(rows) && holds(rows) ? patchRows(rows) : rows,
    );
    void queryClient.invalidateQueries({ queryKey: SCHEDULE_SEARCH_QUERY_KEY, refetchType: 'none' });
}

/** What the editor or the delete dialog shows when a write fails. */
export function scheduleWriteErrorMessage(error: unknown, fallback: string): string {
    const shaped = error as ApiErrorShape | undefined;
    if (shaped?.response?.status === 403) {
        return SCHEDULE_FORBIDDEN_MESSAGE;
    }
    return extractApiErrorMessage(error) ?? shaped?.message ?? fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

type ItemsPatch = (items: unknown) => ScheduleRow[];

function patchListRow(row: unknown, listId: number, patch: ItemsPatch): unknown {
    if (!isRecord(row) || Number(row.id) !== listId) return row;
    return { ...row, [NESTED_SCHEDULES_FIELD]: patch(row[NESTED_SCHEDULES_FIELD]) };
}

/**
 * Apply a patch to the list's nested schedules in every cached search page,
 * the list's inspector record and every drawer that fetched it by id, then
 * mark them stale (see the header).
 */
export function patchCachedListSchedules(queryClient: QueryClient, listId: number, patch: ItemsPatch): void {
    queryClient.setQueriesData({ queryKey: LIST_SEARCH_QUERY_KEY }, (page: unknown) => {
        if (!isRecord(page) || !Array.isArray(page.data)) return page;
        if (!page.data.some((row) => isRecord(row) && Number(row.id) === listId)) return page;
        return { ...page, data: page.data.map((row) => patchListRow(row, listId, patch)) };
    });
    queryClient.setQueriesData({ queryKey: LIST_LIVE_POLL_QUERY_KEY }, (rows: unknown) => {
        if (!Array.isArray(rows) || !rows.some((row) => isRecord(row) && Number(row.id) === listId)) return rows;
        return rows.map((row) => patchListRow(row, listId, patch));
    });
    queryClient.setQueryData(listRecordQueryKey(listId), (record: unknown) =>
        record === undefined ? record : patchListRow(record, listId, patch),
    );
    // The schedule search draws each schedule with its list's card, from this batched copy.
    queryClient.setQueryData(batchedModelRecordQueryKey('LocationZoneCustomPlaylist', listId), (record: unknown) =>
        record === undefined || record === null ? record : patchListRow(record, listId, patch),
    );
    // The drawer's lists. Without this the drawer showed a save only while the
    // pending write lasted, then drew its old cached copy again.
    queryClient.setQueriesData({ queryKey: LIST_RECORDS_BY_ID_QUERY_KEY }, (records: unknown) => {
        if (!(records instanceof Map) || !records.has(String(listId))) return records;
        const next = new Map(records);
        next.set(String(listId), patchListRow(records.get(String(listId)), listId, patch));
        return next;
    });
    void queryClient.invalidateQueries({ queryKey: LIST_SEARCH_QUERY_KEY, refetchType: 'none' });
    void queryClient.invalidateQueries({ queryKey: listRecordQueryKey(listId), refetchType: 'none' });
    void queryClient.invalidateQueries({ queryKey: batchedModelRecordQueryKey('LocationZoneCustomPlaylist', listId), refetchType: 'none' });
    void queryClient.invalidateQueries({ queryKey: LIST_RECORDS_BY_ID_QUERY_KEY, refetchType: 'none' });
}

/**
 * The list the server says the saved row is on. Falls back to the list the
 * write was made from when the response does not say.
 */
export function savedRowListId(saved: Record<string, unknown>, fallbackListId: number): number {
    const id = Number(saved.location_zone_custom_playlist_id);
    return Number.isInteger(id) && id > 0 ? id : fallbackListId;
}

/** Record and patch a created or edited row onto the list it is on, and off the one it left. */
export function applySavedSchedule(queryClient: QueryClient, openedFromListId: number, saved: Record<string, unknown>): void {
    const listId = savedRowListId(saved, openedFromListId);
    recordScheduleSaved(listId, saved);
    patchCachedListSchedules(queryClient, listId, (items) => upsertScheduleItem(items, saved));
    const scheduleId = Number(saved.id);
    if (Number.isInteger(scheduleId) && scheduleId > 0) patchCachedScheduleSearchRows(queryClient, scheduleId, saved);
    if (listId !== openedFromListId && Number.isInteger(scheduleId) && scheduleId > 0) {
        // An edit that the server moved to another list (a row turned into silence).
        recordScheduleDeleted(openedFromListId, scheduleId);
        patchCachedListSchedules(queryClient, openedFromListId, (items) => removeScheduleItem(items, scheduleId));
    }
}

interface ItemEnvelope<T> {
    data: T;
}

function unwrapRow(body: unknown): Record<string, unknown> {
    if (isRecord(body) && isRecord(body.data)) return body.data;
    if (isRecord(body)) return body;
    throw new Error('The server answered without the saved schedule.');
}

export interface CreateScheduleVariables {
    listId: number;
    payload: SchedulePayload;
}

export interface UpdateScheduleVariables {
    listId: number;
    scheduleId: number;
    payload: SchedulePayload;
}

export interface DeleteScheduleVariables {
    listId: number;
    scheduleId: number;
}

export function useCreateSchedule() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ payload }: CreateScheduleVariables) => {
            const response = await http.post<ItemEnvelope<Record<string, unknown>>>(SCHEDULE_RESOURCE, payload);
            return unwrapRow(response.data);
        },
        onSuccess: (saved, { listId }) => {
            applySavedSchedule(queryClient, listId, saved);
        },
    });
}

export function useUpdateSchedule() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ scheduleId, payload }: UpdateScheduleVariables) => {
            const response = await http.put<ItemEnvelope<Record<string, unknown>>>(
                `${SCHEDULE_RESOURCE}/${scheduleId}`,
                payload,
            );
            return { id: scheduleId, ...unwrapRow(response.data) };
        },
        onSuccess: (saved, { listId }) => {
            applySavedSchedule(queryClient, listId, saved);
        },
    });
}

export function useDeleteSchedule() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ scheduleId }: DeleteScheduleVariables) => {
            await http.delete(`${SCHEDULE_RESOURCE}/${scheduleId}`);
            return scheduleId;
        },
        onSuccess: (scheduleId, { listId }) => {
            recordScheduleDeleted(listId, scheduleId);
            patchCachedListSchedules(queryClient, listId, (items) => removeScheduleItem(items, scheduleId));
            patchCachedScheduleSearchRows(queryClient, scheduleId, null);
        },
    });
}
